import { DonorActionBar } from '@components/donor/action-bar';
import { UploadFiles } from '@components/ui/upload-file';
import { useData } from '@hooks/form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getDistanceToViewportBottom } from '@utils/dom';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { DonationExperienceState } from '.';

export const bottomSpace = 20;

export type SettingsPanelProps = {
  isLoading?: boolean;
  onSave(): void;
  useData: ReturnType<typeof useData<DonationExperienceState>>;
};

export const SettingsPanel: FunctionComponent<SettingsPanelProps> = (props) => {
  const { isLoading, onSave, useData } = props;
  const ref = useRef<HTMLDivElement>();
  const [height, setHeight] = useState(1);
  const { data, setValueFromKey } = useData;

  useEffect(() => {
    if (ref.current) {
      setHeight(getDistanceToViewportBottom(ref.current) - bottomSpace);
    }
  }, []);

  const onChangeHeroImage = (files: File[]) => {
    if (files.length > 0) {
      const [file] = files;
      setValueFromKey('landingPageHero')(file);
    }
  };

  const onChangeLogoImage = (files: File[]) => {
    if (files.length > 0) {
      const [file] = files;
      setValueFromKey('landingPageLogo')(file);
    }
  };

  return (
    <Stack direction="column">
      <Stack direction="row" justifyContent="space-between" py={1}>
        <DonorActionBar url={data.previewUrl} />
        <LoadingButton
          loading={isLoading}
          disabled={isLoading}
          onClick={onSave}
        >
          Save
        </LoadingButton>
      </Stack>
      <Divider />

      <Box
        ref={ref}
        sx={{
          height: { lg: height, sm: '100%', xl: height },
          minHeight: { lg: height, sm: '100%', xl: height },
          overflowX: { lg: 'hidden' },
          overflowY: { lg: 'scroll' },
          scrollBehavior: { lg: 'smooth' },
        }}
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id="donation-options"
          >
            <Typography variant="h6">Donation Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              <TextField
                id="donation-title"
                label="Title"
                onChange={(event) =>
                  setValueFromKey('donationTitle')(event.target.value)
                }
                value={data.donationTitle}
              />

              <TextField
                id="donation-description"
                label="Description"
                multiline
                maxRows={4}
                rows={4}
                onChange={(event) =>
                  setValueFromKey('donationDescription')(event.target.value)
                }
                value={data.donationDescription}
              />
              <FormControlLabel
                control={<Checkbox checked={data.donationHideSearchBar} />}
                label="Hide Search Bar"
                onChange={(_event, checked) =>
                  setValueFromKey('donationHideSearchBar')(checked)
                }
              />
              {/* <FormControlLabel
                control={
                  <Checkbox checked={data.donationEmailPrompt === '1'} />
                }
                label="Disable request for email"
                onChange={(_event, checked) =>
                  setValueFromKey('donationEmailPrompt')(
                    checked ? '1' : undefined,
                  )
                }
              /> */}
              <FormControlLabel
                control={<Checkbox checked={data.donationEmailRequired} />}
                label="Email is required"
                onChange={(_event, checked) =>
                  setValueFromKey('donationEmailRequired')(checked)
                }
              />
              <FormControlLabel
                control={<Checkbox checked={data.donationShowUSD} />}
                label="Show USD"
                onChange={(_event, checked) =>
                  setValueFromKey('donationShowUSD')(checked)
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultChecked>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} id="page-options">
            <Typography variant="h6">Page Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              <TextField
                id="page-title"
                label="Title"
                onChange={(event) =>
                  setValueFromKey('landingPageTitle')(event.target.value)
                }
                value={data.landingPageTitle}
              />

              <TextField
                id="page-description"
                label="Description"
                multiline
                maxRows={4}
                rows={4}
                onChange={(event) =>
                  setValueFromKey('landingPageDescription')(event.target.value)
                }
                value={data.landingPageDescription}
              />
              <FormControlLabel
                control={<Checkbox checked={data.landingPageWidgetOnly} />}
                label="Display only the donation widget"
                onChange={(_event, checked) =>
                  setValueFromKey('landingPageWidgetOnly')(checked)
                }
              />
              <Stack spacing={2}>
                <Typography variant="body1">Logo Image</Typography>
                <Stack
                  direction="row"
                  spacing={0}
                  alignContent="center"
                  justifyContent="space-between"
                >
                  <Avatar
                    src={
                      data.landingPageLogo
                        ? window.URL.createObjectURL(data.landingPageLogo)
                        : data.landingPageLogoUrl
                    }
                    variant="rounded"
                    sx={{ height: 'auto', width: '50%' }}
                  />
                  <UploadFiles
                    maxFiles={1}
                    maxFileSizeMB={10}
                    onFilesSelected={onChangeLogoImage}
                  />
                </Stack>
              </Stack>
              <Stack spacing={2}>
                <Typography variant="body1">Hero Image</Typography>
                <Stack
                  direction="row"
                  spacing={0}
                  alignContent="center"
                  justifyContent="space-between"
                >
                  <Avatar
                    src={
                      data.landingPageHero
                        ? window.URL.createObjectURL(data.landingPageHero)
                        : data.landingPageHeroUrl
                    }
                    sx={{ height: 'auto', width: '50%' }}
                    variant="rounded"
                  />
                  <UploadFiles
                    maxFiles={1}
                    maxFileSizeMB={10}
                    onFilesSelected={onChangeHeroImage}
                  />
                </Stack>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Stack>
  );
};
